import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useScroll,
  useSpring,
  useTransform,
  useMotionValue,
} from "framer-motion";

// =============================================================
// Reveal — fade + translate when element enters viewport
// =============================================================
export const Reveal = ({
  children,
  delay = 0,
  y = 40,
  duration = 0.9,
  className,
  once = true,
  as: Component = motion.div,
}) => {
  return (
    <Component
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Component>
  );
};

// =============================================================
// StaggerWords — splits a string into words and reveals each
// =============================================================
export const StaggerWords = ({
  text,
  className,
  delay = 0,
  staggerChildren = 0.06,
  wordClassName,
  highlightClassName = "text-gold-italic",
  highlightWords = [],
}) => {
  const words = (text || "").split(" ");
  return (
    <motion.span
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      transition={{ staggerChildren, delayChildren: delay }}
      style={{ display: "inline-block" }}
    >
      {words.map((w, i) => {
        const isHighlight = highlightWords.includes(w.replace(/[^\p{L}]/gu, ""));
        return (
          <motion.span
            key={i}
            className={`${wordClassName || ""} ${
              isHighlight ? highlightClassName : ""
            }`}
            style={{
              display: "inline-block",
              marginRight: "0.28em",
              whiteSpace: "nowrap",
            }}
            variants={{
              hidden: { opacity: 0, y: "0.6em", filter: "blur(8px)" },
              visible: {
                opacity: 1,
                y: 0,
                filter: "blur(0px)",
                transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
              },
            }}
          >
            {w}
          </motion.span>
        );
      })}
    </motion.span>
  );
};

// =============================================================
// MagneticCTA — magnetic hover effect for CTAs
// =============================================================
export const MagneticCTA = ({ children, className, strength = 18, ...rest }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setPos({ x: dx * strength, y: dy * strength });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => setPos({ x: 0, y: 0 })}
      animate={pos}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
      className={className}
      style={{ display: "inline-block" }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

// =============================================================
// ScrollProgress — top bar fills as page scrolls
// =============================================================
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 22,
    mass: 0.4,
  });
  return (
    <motion.div
      className="hma-scroll-progress-bar"
      style={{
        scaleX,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        transformOrigin: "0%",
        background: "linear-gradient(90deg,#c9a14a,#ecc879)",
        zIndex: 9999,
      }}
    />
  );
};

// =============================================================
// CursorAccent — small gold ring follows cursor
// =============================================================
export const CursorAccent = () => {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 250, damping: 22, mass: 0.3 });
  const sy = useSpring(y, { stiffness: 250, damping: 22, mass: 0.3 });
  const [hidden, setHidden] = useState(true);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handle = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setHidden(false);
    };
    const enter = (e) => {
      const tag = e.target?.tagName;
      if (tag === "A" || tag === "BUTTON" || e.target?.closest?.("a, button"))
        setHovered(true);
    };
    const leave = (e) => {
      const tag = e.target?.tagName;
      if (tag === "A" || tag === "BUTTON" || e.target?.closest?.("a, button"))
        setHovered(false);
    };
    const out = () => setHidden(true);
    window.addEventListener("mousemove", handle);
    document.addEventListener("mouseover", enter);
    document.addEventListener("mouseout", leave);
    document.addEventListener("mouseleave", out);
    return () => {
      window.removeEventListener("mousemove", handle);
      document.removeEventListener("mouseover", enter);
      document.removeEventListener("mouseout", leave);
      document.removeEventListener("mouseleave", out);
    };
  }, [x, y]);

  if (typeof window === "undefined") return null;
  if (window.matchMedia("(hover: none)").matches) return null;

  return (
    <motion.div
      className="hma-cursor-accent"
      style={{
        translateX: sx,
        translateY: sy,
        opacity: hidden ? 0 : 1,
      }}
      animate={{
        scale: hovered ? 2.4 : 1,
        backgroundColor: hovered ? "rgba(201,161,74,0.18)" : "transparent",
      }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
    />
  );
};

// =============================================================
// Parallax — vertical parallax on scroll for backgrounds
// =============================================================
export const Parallax = ({ children, offset = 80, className }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-offset, offset]);
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

// =============================================================
// LuxeIntro — page-load curtain reveal (gold wipe + brand)
// =============================================================
export const LuxeIntro = ({ brand = "HMA", tagline = "ULTIMATE EXCELLENCY" }) => {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const seen = sessionStorage.getItem("hma_intro_seen");
    if (seen) {
      setDone(true);
      return;
    }
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setDone(true);
      sessionStorage.setItem("hma_intro_seen", "1");
      document.body.style.overflow = "";
    }, 2300);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, []);

  if (done) return null;

  return (
    <motion.div
      className="hma-intro"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "#050720",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 50% 50%, rgba(201,161,74,0.18), transparent 60%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 2, ease: "easeInOut" }}
      />
      <motion.div
        initial={{ opacity: 0, letterSpacing: "0.4em" }}
        animate={{ opacity: 1, letterSpacing: "0.18em" }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{
          fontFamily: "Cormorant Garamond, Fraunces, serif",
          fontSize: "clamp(3.5rem, 12vw, 9rem)",
          color: "#f1ebd9",
          fontWeight: 500,
          lineHeight: 1,
          textAlign: "center",
        }}
      >
        {brand}
      </motion.div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: "180px",
          height: "1px",
          background: "#c9a14a",
          marginTop: 24,
          marginBottom: 18,
          transformOrigin: "center",
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.95 }}
        style={{
          fontFamily: "DM Sans, sans-serif",
          letterSpacing: "0.42em",
          fontSize: 11,
          color: "#c9a14a",
          textTransform: "uppercase",
        }}
      >
        — {tagline} —
      </motion.div>
      <motion.div
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, delay: 1.6, ease: [0.83, 0, 0.17, 1] }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: "#050720",
          transformOrigin: "bottom",
          pointerEvents: "none",
        }}
      />
    </motion.div>
  );
};

// =============================================================
// OrnamentalDivider — gold hairline with central diamond
// =============================================================
export const OrnamentalDivider = ({ width = 280, color = "#c9a14a" }) => (
  <motion.svg
    width={width}
    height="14"
    viewBox={`0 0 ${width} 14`}
    initial={{ opacity: 0, scale: 0.85 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    style={{ display: "block", margin: "0 auto" }}
  >
    <line
      x1="0"
      y1="7"
      x2={width / 2 - 14}
      y2="7"
      stroke={color}
      strokeWidth="1"
    />
    <line
      x1={width / 2 + 14}
      y1="7"
      x2={width}
      y2="7"
      stroke={color}
      strokeWidth="1"
    />
    <g transform={`translate(${width / 2}, 7) rotate(45)`}>
      <rect x="-5" y="-5" width="10" height="10" stroke={color} fill="none" strokeWidth="1" />
    </g>
  </motion.svg>
);

// =============================================================
// GoldSeal — vintage rotating circular seal
// =============================================================
export const GoldSeal = ({
  topText = "EST · 2009 · DOHA",
  bottomText = "ULTIMATE · EXCELLENCY",
  centerLabel = "HMA",
  size = 120,
}) => {
  return (
    <div className="hma-seal" style={{ width: size, height: size }}>
      <motion.svg
        viewBox="0 0 100 100"
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}
      >
        <defs>
          <path id="seal-top" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0" />
          <path id="seal-bot" d="M 50,50 m -38,0 a 38,38 0 1,0 76,0" />
        </defs>
        <circle cx="50" cy="50" r="45" stroke="#c9a14a" fill="none" strokeWidth="0.6" />
        <circle cx="50" cy="50" r="40" stroke="#c9a14a" fill="none" strokeWidth="0.4" strokeDasharray="1 2" />
        <text fill="#c9a14a" fontSize="6.5" letterSpacing="2" fontFamily="DM Sans">
          <textPath href="#seal-top" startOffset="50%" textAnchor="middle">
            {topText}
          </textPath>
        </text>
        <text fill="#c9a14a" fontSize="6.5" letterSpacing="2" fontFamily="DM Sans">
          <textPath href="#seal-bot" startOffset="50%" textAnchor="middle">
            {bottomText}
          </textPath>
        </text>
      </motion.svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Cormorant Garamond, serif",
          fontSize: size * 0.32,
          color: "#ecc879",
          fontStyle: "italic",
          fontWeight: 500,
          letterSpacing: "-0.02em",
        }}
      >
        {centerLabel}
      </div>
    </div>
  );
};

// =============================================================
// KineticMarquee — looping serif italic ticker with diamonds
// =============================================================
export const KineticMarquee = ({ items = [], speed = 40, italic = true }) => {
  const repeated = [...items, ...items, ...items];
  return (
    <div className="hma-marquee">
      <motion.div
        className="hma-marquee__track"
        animate={{ x: ["0%", "-33.333%"] }}
        transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
        style={{ willChange: "transform" }}
      >
        {repeated.map((it, i) => (
          <span key={i} className="hma-marquee__item">
            <span style={{ fontStyle: italic ? "italic" : "normal" }}>{it}</span>
            <svg width="14" height="14" viewBox="0 0 14 14" className="hma-marquee__diamond">
              <rect
                x="2"
                y="2"
                width="10"
                height="10"
                transform="rotate(45 7 7)"
                stroke="#c9a14a"
                fill="none"
                strokeWidth="1"
              />
            </svg>
          </span>
        ))}
      </motion.div>
    </div>
  );
};

// =============================================================
// TiltCard — 3D tilt on mouse + spotlight
// =============================================================
export const TiltCard = ({ children, className, max = 8 }) => {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const handleMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: dx * max, y: dy * max });
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      animate={{ rotateY: tilt.x, rotateX: -tilt.y }}
      transition={{ type: "spring", stiffness: 200, damping: 22 }}
      style={{ transformStyle: "preserve-3d", perspective: 1000 }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================
// MaskReveal — image reveal with vertical clip-path
// =============================================================
export const MaskReveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ clipPath: "inset(100% 0 0 0)" }}
    whileInView={{ clipPath: "inset(0% 0 0 0)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.1, delay, ease: [0.65, 0, 0.35, 1] }}
    style={{ overflow: "hidden" }}
  >
    {children}
  </motion.div>
);

// =============================================================
// AnimatedCounter — counts up to value when in view
// =============================================================
export const AnimatedCounter = ({
  value,
  suffix = "",
  duration = 1.6,
  className,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const target =
      typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  const formatted =
    typeof value === "string" && value.includes(",")
      ? n.toFixed(1).replace(".", ",")
      : Math.round(n).toString();

  return (
    <span ref={ref} className={className}>
      {formatted}
      {suffix}
    </span>
  );
};
